import { Button, CircularProgress, Container, FormControl, InputLabel, MenuItem, Select, Typography } from "@material-ui/core";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import React, { useState, useReducer, useEffect } from "react";
import { AccentsAndDialectsDD } from "../../../globalConstants";
import translate from "../../../i18n/translate";
import "./AccentsAndDialects.scss";
import ReactAudioPlayer from "react-audio-player";
import useBrowserStorage from "../../shared/StoreLocalInfo";
import { getRandomNumber } from "../../shared/utils/sharedUtils";

const AccentsAndDialects: React.FC = () => {
  const ADcountryLists = AccentsAndDialectsDD[0].ContinentMap;
  const continents = AccentsAndDialectsDD[0].ContinentList;
  const defaultContinent = continents[0];
  const [loader, setLoader] = useState(false);
  const [isStateDisabled, setIsStateDisabled] = useState(false);
  const [topicLocation, setTopicLocation] = useState<any>();
  const [topicBody, setTopicBody] = useState<any>();
  const [topics, setTopics] = useState<any>();
  const [countries, setCountries] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [sessionContinent, setsessionContinent] = useBrowserStorage("accentsContinentSelected", "");
  const [sessionCountry, setsessionCountry] = useBrowserStorage("accentscountrySelected", "");
  const [sessionState, setsessionState] = useBrowserStorage("accentsstateSelected", "");
  const [sessionTopic, setsessionTopic] = useBrowserStorage("topicSelected", "");
  
  
  /** use effects **/
  useEffect(() => {
    getContinentList(defaultContinent);
  }, []);

  /** toast handler functions **/
  const [, settoastOptions] = useReducer((state: any, newState: any) => ({ ...state, ...newState }), {
    toastOpen: false,
    toastSeverity: "error",
    toastMessageKey: "",
  });

  const handleToastOptionsDirect = (name: string, val: any) => {
    settoastOptions({ [name]: val });
  };

  const toasterHandle = (open: boolean, severity: string, messageKey: string) => {
    handleToastOptionsDirect("toastSeverity", severity);
    handleToastOptionsDirect("toastMessageKey", messageKey);
    handleToastOptionsDirect("toastOpen", open);
  };

  /**
   * Make api to get topic content data
   * @param topicID 
   * @returns 
   */
  async function topicJsonData(topicID: string) {
    setLoader(true);
    let topicList = await fetch(`${process.env.REACT_APP_BLOB_STORAGE}/accentsanddialects/${topicID}.json`)
                        .then((res: any) => res.json())
                        .then((data: any) => {
                          setLoader(false);
                          return data;
                        })
                        .catch((err: any) => {
                          setLoader(false);
                          console.log(err, "topicJsonData")
                          toasterHandle(true, "error", "message_errorDefault");
                        });
    return topicList;
  }

  /**
   * On change event for select dropdown
   * @param event 
   */
  const handleChangeContinentsValue = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    getContinentList(event.target.value)
  }

  const handleChangeCountriesValue = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    getCountriesList(event.target.value)
  }

  const handleChangeStatesValue = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    getStatesList(event.target.value)
  }

  const handleChangeTopicsValue = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    getTopicsList(event.target.value)
  }

  /**
   * Custom functions to form data 
   * @param continent
   * @param country
   * @param state
   * @param topic
   */
  const getContinentList = async (continent: string) => {
    const countryList = ADcountryLists.filter((data) => data.Name === continent);
    setsessionContinent(continent);
    setCountries(countryList[0].countries);
    setStates(countryList[0].states);
    getCountriesList(countryList[0].countries[0], continent);
    if (countryList[0].states.length) setsessionState(countryList[0].states[0]);
  }

  const getCountriesList = async (country: string, continent: string = "") => {
    setsessionCountry(country);
    const isCountryUS = country === "US";
    const currentContinent = continent || sessionContinent;
    const topicID: string = isCountryUS ? `${country}${sessionState}` 
                                        : `${currentContinent}${country}`;
    const localeTopicData: any = await topicJsonData(topicID);
    if (!localeTopicData) return;
    setIsStateDisabled(isCountryUS);
    setTopicLocation(localeTopicData);
    getTopicsList(localeTopicData.meta.titles[0].title, localeTopicData);
  }

  const getStatesList = async (state: string) => {
    setsessionState(state);
    const topicID: string = `${sessionCountry}${state}`;
    const localeTopicData: any = await topicJsonData(topicID);
    if (!localeTopicData) return;
    getTopicsList(localeTopicData.meta.titles[0].title, localeTopicData);
    setTopicLocation(localeTopicData);
  }

  const getTopicsList = (topic: string, newTopicData: any = undefined) => {
    const newTopicList = newTopicData ?? topicLocation;
    setsessionTopic(topic);
    setTopics(topic);
    const articlesList = newTopicList.meta.titles.filter((data: any) => data.title === topic);
    const CNtopic = newTopicList.items.filter((data: any) => data.id === articlesList[0].id);
    setTopicBody(CNtopic[0]);
  }

  /** Buttons click event **/
  const buttonNextClick = () => {
    if (topicBody.id < topicLocation.items.length) {
      const nextarticlesList = topicLocation.meta.titles.filter(
        (data: any) => data.id === topicLocation.items[topicBody.id + 1].id
      );
      setsessionTopic(nextarticlesList[0].title);
      setTopicBody(topicLocation.items[topicBody.id + 1]);
      setTopics(nextarticlesList[0].title);
    }
  };

  return (
    <div data-testid="accentsId" className="accents">
      <Container>
        <div className="accents-title">
          <h3>{translate("accentsAndDialects_heading")}</h3>
        </div>

        <div className={isStateDisabled ? "accents-location hasstate" : "accents-location"}>
            <div className="accents-location-selectbox">
              <FormControl variant="outlined" >
                <InputLabel id="continent-label">{translate("SAR")}</InputLabel>
                <Select
                  labelId="continent-label"
                  id="Continent"
                  data-testid="continentId"
                  value={sessionContinent || ""}
                  onChange={(event: any) => handleChangeContinentsValue(event)}
                  label={translate("SAR")}
                >
                  {continents.map((option: any, index: number) => {
                    return <MenuItem key={getRandomNumber(index)} value={option}>{translate(option)}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </div>

            <div className="accents-location-selectbox">
              <FormControl variant="outlined" >
                <InputLabel id="countries-label">{translate("SAL")}</InputLabel>
                <Select
                  labelId="countries-label"
                  id="Countries"
                  data-testid="Countries"
                  value={sessionCountry || ""}
                  onChange={(event: any) => handleChangeCountriesValue(event)}
                  label={translate("SAL")}
                >
                  {countries.map((option: any, index: number) => {
                    return <MenuItem key={getRandomNumber(index)} value={option}>{translate(option)}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </div>

            { isStateDisabled && (
                <div className="accents-location-selectbox">
                  <FormControl variant="outlined" >
                    <InputLabel id="states-label">
                      {translate("profile_statePlaceholder")}
                    </InputLabel>
                    <Select
                      labelId="states-label"
                      id="States"
                      data-testid="states-labeID"
                      value={sessionState || ""}
                      onChange={(event: any) => handleChangeStatesValue(event)}
                      label={translate("profile_statePlaceholder")}
                    >
                      {states.map((option: any, index: number) => {
                        return <MenuItem key={getRandomNumber(index)} value={option}>{translate(option)}</MenuItem>;
                      })}
                    </Select>
                  </FormControl>
                </div>
              )
            }
            
            <div className="accents-location-selectbox">
              { topicLocation && topics &&
                <FormControl variant="outlined" >
                  <InputLabel id="topics-label">{translate("SAT")}</InputLabel>
                  <Select
                    labelId="topics-label"
                    id="Topics"
                    value={topics}
                    onChange={(event: any) => handleChangeTopicsValue(event)}
                    label={translate("SAT")}
                  >
                    { topicLocation?.meta?.titles?.map((option: any) => {
                        return (
                          <MenuItem key={option.id} id={option.id} value={option.title}>
                            {option.title}
                          </MenuItem>
                        );
                      })
                    }
                  </Select>
                </FormControl>
              }
            </div>
        </div>
        <div className="accents-content">
          { topicBody && !loader ? (
            <React.Fragment>
              <div className="accents-content-container">
                <div className="accents-content-heading">
                  <h2>
                    {translate(sessionContinent || "")}:{" "}
                    {translate(sessionCountry || "")}{isStateDisabled ? " - " : " "}
                    {isStateDisabled ? translate(sessionState || "") : " "}
                  </h2>
                </div>
                <div className="accents-content-player">
                  <div className="accents-content-player-heading"><PersonOutlineIcon></PersonOutlineIcon>{sessionTopic || ""}</div>
                  <div className="accents-content-player-audio">
                    <ReactAudioPlayer
                      src={process.env.REACT_APP_CN_BLOBSTORAGE + topicBody.Audio}
                      controls={true}
                      loop={false}
                      controlsList="nodownload"
                      className="accents-content-player-render"
                    />
                  </div>
                </div>
                <h2 className="accents-topic-header">{translate(topicBody.title)}</h2>
                <div className="accents-topic-para" dangerouslySetInnerHTML={{ __html: topicBody.body }} />
              </div>
              <div className={topicBody.id === topicLocation.items.length - 1 ? "d-none" : "accents-next-pagination"}>
                <Button type="button" variant="contained" color="primary" onClick={buttonNextClick}>
                  <Typography variant="subtitle2">{translate("button_next")}</Typography>
                </Button>
              </div>
            </React.Fragment>
          ) : 
            <div className="accents-loader-container">
              <CircularProgress size={50} color="primary" />
            </div>
          }
        </div>
      </Container>
    </div>
  );
};

export default AccentsAndDialects;
